import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import type { FileAttachment, Item, Note } from "../api/types";
import { useT } from "../i18n";
import { colorHex, PRIORITY_COLORS } from "../lib/colors";
import { deadlineLabel, dueLabel } from "../lib/dates";
import { Markdown, renderInline } from "../lib/markdown";
import { childrenOf } from "../lib/tasks";
import { useApp, useEngine, useSyncState } from "../store";
import {
  addComment,
  addReminder,
  completeTask,
  deleteComment,
  deleteReminder,
  deleteTask,
  moveTaskToProject,
  uncompleteTask,
  updateTask,
} from "../sync/actions";
import {
  IconBell,
  IconCalendar,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconClose,
  IconFlag,
  IconHash,
  IconInbox,
  IconLabel,
  IconMore,
  IconPaperclip,
  IconTarget,
  IconTrash,
  IconUser,
} from "../ui/icons";
import { DatePicker, LabelPicker, PriorityMenu, ProjectPicker } from "../ui/pickers";
import { usePrefs } from "../ui/prefs";
import { Avatar, Confirm, Menu, Modal, Popover, useAnchor } from "../ui/primitives";
import { Checkbox, openTask } from "./TaskRow";
import { AddTask, TaskTree } from "./TaskTree";

function closeTask(navigate: ReturnType<typeof useNavigate>) {
  const url = new URL(window.location.href);
  url.searchParams.delete("task");
  navigate(`${url.pathname}${url.search}`);
}

/** Neighbor task ids in the order rows appear in the current view. */
function neighbors(id: string): { prev?: string; next?: string } {
  const ids = [...document.querySelectorAll<HTMLElement>(".content [data-task-id]")].map((e) => e.dataset.taskId ?? "");
  const i = ids.indexOf(id);
  return i < 0 ? {} : { prev: ids[i - 1], next: ids[i + 1] };
}

export function TaskDetail({ taskId }: { taskId: string }) {
  const engine = useEngine();
  const state = useSyncState();
  const t = useT();
  const navigate = useNavigate();
  const id = engine.resolveId(taskId);
  const item = state.items[id];
  const close = () => closeTask(navigate);

  useEffect(() => {
    if (id !== taskId) openTask(navigate, id);
  }, [id, taskId, navigate]);

  if (!item) {
    return (
      <Modal onClose={close} className="detail" label="task">
        <div className="detail-top">
          <span />
          <button type="button" className="icon-btn" onClick={close} aria-label={t("close")}>
            <IconClose size={20} />
          </button>
        </div>
        <div className="empty-state">{state.syncToken === "*" ? "…" : t("noTasks")}</div>
      </Modal>
    );
  }
  return <TaskDetailBody item={item} onClose={close} />;
}

function TaskDetailBody({ item, onClose }: { item: Item; onClose: () => void }) {
  const state = useSyncState();
  const t = useT();
  const navigate = useNavigate();
  const project = state.projects[item.project_id];
  const section = item.section_id ? state.sections[item.section_id] : null;
  const parent = item.parent_id ? state.items[item.parent_id] : null;
  const { prev, next } = neighbors(item.id);
  const more = useAnchor();
  const [confirm, setConfirm] = useState(false);

  return (
    <Modal onClose={onClose} className="detail" label={t("tasks")}>
      <div className="detail-top">
        <div className="breadcrumb" style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
          {project?.inbox_project ? (
            <IconInbox size={16} />
          ) : (
            <span style={{ color: colorHex(project?.color), display: "inline-flex" }}>
              <IconHash size={16} />
            </span>
          )}
          <span>{project?.inbox_project ? t("inbox") : project?.name}</span>
          {section && <span>/ {section.name}</span>}
          {parent && (
            <button type="button" className="link" onClick={() => openTask(navigate, parent.id)}>
              / {renderInline(parent.content, "p", true)}
            </button>
          )}
        </div>
        <div style={{ display: "flex", gap: 2 }}>
          <button
            type="button"
            className="icon-btn"
            disabled={!prev}
            onClick={() => prev && openTask(navigate, prev)}
            aria-label={t("previous")}
          >
            <IconChevronLeft size={20} />
          </button>
          <button
            type="button"
            className="icon-btn"
            disabled={!next}
            onClick={() => next && openTask(navigate, next)}
            aria-label={t("next")}
          >
            <IconChevronRight size={20} />
          </button>
          <button type="button" className="icon-btn" onClick={more.open} aria-label={t("more")}>
            <IconMore size={20} />
          </button>
          <button
            type="button"
            className="icon-btn"
            onClick={onClose}
            aria-label={t("close")}
            data-testid="detail-close"
          >
            <IconClose size={20} />
          </button>
        </div>
      </div>
      <div className="detail-cols">
        <div className="detail-main" data-testid="task-detail">
          <div className="detail-title-row">
            <Checkbox
              priority={item.priority}
              checked={item.checked}
              label={t("completed")}
              onToggle={() => (item.checked ? void uncompleteTask(item.id) : completeTask(item))}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <EditableText
                value={item.content}
                className="detail-title"
                testId="detail-title"
                onSave={(v) => v.trim() && v !== item.content && void updateTask(item.id, { content: v.trim() })}
                render={(v) => renderInline(v)}
              />
              <EditableText
                value={item.description}
                multiline
                className={`detail-desc${item.description ? "" : " empty"}`}
                testId="detail-description"
                placeholder={t("description")}
                onSave={(v) => v !== item.description && void updateTask(item.id, { description: v })}
                render={(v) => (v ? <Markdown text={v} /> : t("description"))}
              />
            </div>
          </div>
          <SubTasks item={item} />
          <Comments item={item} />
        </div>
        <SidePanel item={item} />
      </div>
      {more.anchor && (
        <Menu
          anchor={more.anchor}
          onClose={more.close}
          placement="bottom-end"
          items={[
            {
              key: "delete",
              label: t("delete"),
              danger: true,
              icon: <IconTrash size={18} />,
              onSelect: () => setConfirm(true),
            },
          ]}
        />
      )}
      {confirm && (
        <Confirm
          message={t("deleteTaskConfirm")}
          confirmLabel={t("delete")}
          cancelLabel={t("cancel")}
          onConfirm={() => {
            void deleteTask(item.id);
            onClose();
          }}
          onClose={() => setConfirm(false)}
        />
      )}
    </Modal>
  );
}

function EditableText({
  value,
  onSave,
  render,
  className,
  multiline,
  placeholder,
  testId,
}: {
  value: string;
  onSave: (v: string) => void;
  render: (v: string) => React.ReactNode;
  className: string;
  multiline?: boolean;
  placeholder?: string;
  testId?: string;
}) {
  const t = useT();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (editing && ref.current) {
      ref.current.style.height = "0px";
      ref.current.style.height = `${ref.current.scrollHeight + 2}px`;
    }
  });
  if (!editing) {
    return (
      <div
        className={className}
        data-testid={testId}
        onClick={(e) => {
          if ((e.target as HTMLElement).closest("a")) return;
          setDraft(value);
          setEditing(true);
        }}
      >
        {render(value)}
      </div>
    );
  }
  const save = () => {
    onSave(draft);
    setEditing(false);
  };
  return (
    <div className="editor-card" style={{ margin: "4px 0 8px -5px" }}>
      <textarea
        ref={ref}
        className="desc-input"
        style={multiline ? { minHeight: 60, fontSize: 14 } : { fontSize: 18, fontWeight: 600 }}
        autoFocus
        value={draft}
        placeholder={placeholder}
        data-testid={testId ? `${testId}-input` : undefined}
        onChange={(e) => setDraft(multiline ? e.target.value : e.target.value.replace(/\n/g, " "))}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            e.preventDefault();
            setEditing(false);
          }
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey || (!multiline && !e.shiftKey))) {
            e.preventDefault();
            save();
          }
        }}
      />
      <div className="editor-footer" style={{ justifyContent: "flex-end" }}>
        <button type="button" className="btn btn-secondary" onClick={() => setEditing(false)}>
          {t("cancel")}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={save}
          data-testid={testId ? `${testId}-save` : undefined}
        >
          {t("save")}
        </button>
      </div>
    </div>
  );
}

function SubTasks({ item }: { item: Item }) {
  const state = useSyncState();
  const t = useT();
  const children = childrenOf(state, item.id, true);
  const done = children.filter((c) => c.checked).length;
  return (
    <div>
      <div className="detail-section-title">
        {t("subTasks")}
        {children.length > 0 && (
          <span className="muted small">
            {done}/{children.length}
          </span>
        )}
      </div>
      <div style={{ marginLeft: 28 }} data-testid="subtasks">
        {children.map((c) => (
          <TaskTree key={c.id} item={c} showCompleted />
        ))}
        <AddTask
          defaults={{ projectId: item.project_id, sectionId: item.section_id, parentId: item.id }}
          label={t("addSubTask")}
        />
      </div>
    </div>
  );
}

function relTime(iso: string, lang: string): string {
  try {
    return new Date(iso).toLocaleString(lang === "zh" ? "zh-CN" : "en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function Attachment({ a }: { a: FileAttachment }) {
  const isImage = a.file_type?.startsWith("image/") || a.resource_type === "image";
  return (
    <a className="attachment" href={a.file_url} target="_blank" rel="noopener noreferrer" data-testid="attachment">
      {isImage ? <img src={a.image ?? a.file_url} alt={a.file_name} /> : <IconPaperclip size={16} />}
      {!isImage && <span>{a.file_name}</span>}
    </a>
  );
}

function Comments({ item }: { item: Item }) {
  const state = useSyncState();
  const prefs = usePrefs();
  const t = useT();
  const client = useApp((s) => s.client);
  const me = state.user;
  const notes = Object.values(state.notes)
    .filter((n) => n.item_id === item.id)
    .sort((a, b) => (a.posted_at < b.posted_at ? -1 : 1));
  const [text, setText] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const author = (n: Note) =>
    n.posted_uid === me?.id
      ? (me?.full_name ?? "")
      : (state.collaborators[n.posted_uid]?.full_name ?? t("unknownUser"));
  const submit = async () => {
    if (!text.trim() && !file) return;
    setBusy(true);
    setError(null);
    try {
      let attachment: FileAttachment | undefined;
      if (file) {
        const form = new FormData();
        form.append("file", file);
        form.append("file_name", file.name);
        attachment = await client.upload<FileAttachment>("/uploads", form);
      }
      void addComment(item.id, text.trim(), attachment as Record<string, unknown> | undefined);
      setText("");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <div className="detail-section-title">
        {t("comments")}
        {notes.length > 0 && <span className="muted small">{notes.length}</span>}
      </div>
      <div data-testid="comments">
        {notes.map((n) => (
          <div className="comment" key={n.id} data-testid="comment">
            <Avatar name={author(n)} size={28} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="meta">
                <span className="who">{author(n)}</span>
                <span>{relTime(n.posted_at, prefs.lang)}</span>
                {n.posted_uid === me?.id && (
                  <button
                    type="button"
                    className="icon-btn"
                    style={{ marginLeft: "auto", width: 22, height: 22 }}
                    onClick={() => void deleteComment(n.id)}
                    aria-label={t("delete")}
                  >
                    <IconTrash size={14} />
                  </button>
                )}
              </div>
              {n.content && <Markdown text={n.content} />}
              {n.file_attachment && <Attachment a={n.file_attachment} />}
            </div>
          </div>
        ))}
      </div>
      <div className="comment-box">
        <textarea
          placeholder={t("comment")}
          value={text}
          data-testid="comment-input"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void submit();
            }
          }}
        />
        {file && (
          <div className="small muted" style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <IconPaperclip size={14} /> {file.name}
            <button
              type="button"
              className="icon-btn"
              style={{ width: 20, height: 20 }}
              onClick={() => setFile(null)}
              aria-label={t("delete")}
            >
              <IconClose size={12} />
            </button>
          </div>
        )}
        {error && <div className="error-text">{error}</div>}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
          <label className="icon-btn" title={t("attach")} aria-label={t("attach")}>
            <IconPaperclip size={18} />
            <input
              ref={fileRef}
              type="file"
              hidden
              data-testid="comment-file"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </label>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={busy || (!text.trim() && !file)}
            onClick={() => void submit()}
            data-testid="comment-submit"
          >
            {busy ? t("uploading") : t("comment")}
          </button>
        </div>
      </div>
    </div>
  );
}

function SideField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="side-field">
      <div className="label">{label}</div>
      {children}
    </div>
  );
}

function SidePanel({ item }: { item: Item }) {
  const state = useSyncState();
  const prefs = usePrefs();
  const t = useT();
  const proj = useAnchor();
  const date = useAnchor();
  const deadline = useAnchor();
  const prio = useAnchor();
  const labels = useAnchor();
  const assign = useAnchor();
  const remind = useAnchor();
  const project = state.projects[item.project_id];
  const section = item.section_id ? state.sections[item.section_id] : null;
  const due = item.due ? dueLabel(item.due, prefs) : null;
  const dl = item.deadline ? deadlineLabel(item.deadline, prefs) : null;
  const reminders = Object.values(state.reminders).filter((r) => r.item_id === item.id);
  const members = [
    ...(state.user ? [{ id: state.user.id, full_name: state.user.full_name }] : []),
    ...Object.values(state.collaboratorStates)
      .filter((c) => c.project_id === item.project_id && c.user_id !== state.user?.id)
      .map((c) => state.collaborators[c.user_id])
      .filter((c): c is NonNullable<typeof c> => Boolean(c)),
  ];
  const shared = Boolean(project?.is_shared || project?.workspace_id) && members.length > 1;
  const assignee = item.responsible_uid
    ? (members.find((m) => m.id === item.responsible_uid) ?? state.collaborators[item.responsible_uid])
    : null;
  const [reminderText, setReminderText] = useState("");
  return (
    <div className="detail-side">
      <SideField label={t("project")}>
        <button type="button" className="side-btn" onClick={proj.open} data-testid="detail-project">
          {project?.inbox_project ? (
            <IconInbox size={16} />
          ) : (
            <span style={{ color: colorHex(project?.color), display: "inline-flex" }}>
              <IconHash size={16} />
            </span>
          )}
          {project?.inbox_project ? t("inbox") : project?.name}
          {section ? ` / ${section.name}` : ""}
          <span style={{ marginLeft: "auto", display: "inline-flex" }}>
            <IconChevronDown size={14} />
          </span>
        </button>
      </SideField>
      <SideField label={t("dueDate")}>
        <button type="button" className="side-btn" onClick={date.open} data-testid="detail-date">
          <span
            className={due ? `tone-${due.tone}` : "muted"}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <IconCalendar size={16} />
            {due ? due.text : t("noDate")}
          </span>
        </button>
      </SideField>
      <SideField label={t("deadline")}>
        <button type="button" className="side-btn" onClick={deadline.open} data-testid="detail-deadline">
          <span
            className={dl ? `tone-${dl.tone === "overdue" ? "overdue" : "later"}` : "muted"}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <IconTarget size={16} />
            {dl ? dl.text : t("noDate")}
          </span>
        </button>
      </SideField>
      <SideField label={t("priority")}>
        <button type="button" className="side-btn" onClick={prio.open} data-testid="detail-priority">
          <span style={{ color: PRIORITY_COLORS[item.priority], display: "inline-flex" }}>
            <IconFlag size={16} filled={item.priority > 1} />
          </span>
          P{5 - item.priority}
        </button>
      </SideField>
      <SideField label={t("labels")}>
        <button
          type="button"
          className="side-btn"
          onClick={labels.open}
          data-testid="detail-labels"
          style={{ flexWrap: "wrap" }}
        >
          {item.labels.length ? (
            item.labels.map((l) => {
              const label = Object.values(state.labels).find((x) => x.name === l);
              return (
                <span key={l} className="label-chip" style={{ color: colorHex(label?.color) }}>
                  <IconLabel size={14} />
                  <span style={{ color: "var(--text)" }}>{l}</span>
                </span>
              );
            })
          ) : (
            <span className="muted">+</span>
          )}
        </button>
      </SideField>
      <SideField label={t("reminders")}>
        {reminders.map((r) => (
          <div key={r.id} className="side-btn" style={{ cursor: "default" }}>
            <IconBell size={16} />
            <span style={{ flex: 1 }}>
              {r.type === "relative"
                ? prefs.lang === "zh"
                  ? `提前 ${r.minute_offset ?? 0} 分钟`
                  : `${r.minute_offset ?? 0} min before`
                : r.due
                  ? dueLabel(r.due, prefs).text
                  : r.type}
            </span>
            <button
              type="button"
              className="icon-btn"
              style={{ width: 20, height: 20 }}
              onClick={() => void deleteReminder(r.id)}
              aria-label={t("delete")}
            >
              <IconClose size={12} />
            </button>
          </div>
        ))}
        <button type="button" className="side-btn muted" onClick={remind.open}>
          + {t("addReminder")}
        </button>
      </SideField>
      {shared && (
        <SideField label={t("assignee")}>
          <button type="button" className="side-btn" onClick={assign.open} data-testid="detail-assignee">
            {assignee ? <Avatar name={assignee.full_name} size={20} /> : <IconUser size={16} />}
            {assignee ? assignee.full_name : t("unassigned")}
          </button>
        </SideField>
      )}
      {proj.anchor && (
        <ProjectPicker
          anchor={proj.anchor}
          onClose={proj.close}
          value={{ projectId: item.project_id, sectionId: item.section_id }}
          onChange={(target) => void moveTaskToProject(item.id, target.projectId, target.sectionId)}
        />
      )}
      {date.anchor && (
        <DatePicker
          anchor={date.anchor}
          onClose={date.close}
          value={item.due}
          onChange={(d) => void updateTask(item.id, { due: d })}
        />
      )}
      {deadline.anchor && (
        <DatePicker
          anchor={deadline.anchor}
          onClose={deadline.close}
          allowRecurring={false}
          value={
            item.deadline
              ? { date: item.deadline.date, string: "", lang: "en", is_recurring: false, timezone: null }
              : null
          }
          onChange={(d) => {
            if (!d) void updateTask(item.id, { deadline: null });
            else if ("date" in d) void updateTask(item.id, { deadline: { date: d.date } });
          }}
        />
      )}
      {prio.anchor && (
        <PriorityMenu
          anchor={prio.anchor}
          onClose={prio.close}
          value={item.priority}
          onChange={(p) => void updateTask(item.id, { priority: p })}
        />
      )}
      {labels.anchor && (
        <LabelPicker
          anchor={labels.anchor}
          onClose={labels.close}
          value={item.labels}
          onChange={(l) => void updateTask(item.id, { labels: l })}
        />
      )}
      {assign.anchor && (
        <Menu
          anchor={assign.anchor}
          onClose={assign.close}
          items={[
            {
              key: "none",
              label: t("unassigned"),
              checked: !item.responsible_uid,
              onSelect: () => void updateTask(item.id, { responsible_uid: null }),
            },
            ...members.map((m) => ({
              key: m.id,
              label: m.full_name,
              icon: <Avatar name={m.full_name} size={20} />,
              checked: item.responsible_uid === m.id,
              onSelect: () => void updateTask(item.id, { responsible_uid: m.id }),
            })),
          ]}
        />
      )}
      {remind.anchor && (
        <Popover anchor={remind.anchor} onClose={remind.close}>
          <form
            style={{ padding: 8, width: 260 }}
            onSubmit={(e) => {
              e.preventDefault();
              const s = reminderText.trim();
              if (!s) return;
              const rel =
                /^(\d+)\s*(m|min|mins|minutes?|分钟)?\s*(before|前)?$/i.exec(s) ?? /^提前\s*(\d+)\s*分钟$/.exec(s);
              if (rel) void addReminder(item.id, { type: "relative", minute_offset: Number(rel[1]) });
              else void addReminder(item.id, { type: "absolute", due: { string: s } });
              setReminderText("");
              remind.close();
            }}
          >
            <input
              className="input"
              autoFocus
              value={reminderText}
              placeholder={t("reminderPlaceholder")}
              onChange={(e) => setReminderText(e.target.value)}
            />
          </form>
        </Popover>
      )}
    </div>
  );
}
